import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  AvatarGroup,
  Chip,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowBackIos as ArrowLeftIcon,
  ArrowForwardIos as ArrowRightIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import CardModal from './CardModal';

interface KanbanCardProps {
  card: {
    id: string;
    title: string;
    description?: string;
    position: number;
    due_date?: string;
    is_completed?: boolean;
    cover_image_url?: string;
    members?: string[];
    labels?: string[];
    client_id?: string;
    client?: {
      id: string;
      company_name: string;
      contact_name?: string;
      email?: string;
    };
  };
  prevList?: any | null;
  nextList?: any | null;
  onMoveCard?: (cardId: string, targetListId: string) => void;
  onMoveCardUp?: (cardId: string) => void;
  onMoveCardDown?: (cardId: string) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}

const KanbanCard: React.FC<KanbanCardProps> = ({ 
  card, 
  prevList, 
  nextList, 
  onMoveCard,
  onMoveCardUp,
  onMoveCardDown,
  canMoveUp = false,
  canMoveDown = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';
  const [openModal, setOpenModal] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: card.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isOverdue = card.due_date
    ? (() => {
        const dateStr = card.due_date;
        let dateOnly = dateStr;
        if (dateStr.includes('T')) {
          dateOnly = dateStr.split('T')[0];
        }
        const [year, month, day] = dateOnly.split('-').map(Number);
        const dueDate = new Date(Date.UTC(year, month - 1, day));
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return dueDate < today && !card.is_completed;
      })()
    : false;

  return (
    <>
      <Box
        ref={setNodeRef}
        style={style}
        {...attributes}
        sx={{
          p: isMobile ? 1.25 : 1.5,
          mb: 1,
          position: 'relative',
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: isDragging ? theme.shadows[8] : theme.shadows[1],
          transform: isDragging ? 'rotate(3deg)' : undefined,
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: theme.shadows[4],
            borderColor: theme.palette.primary.main,
            transform: 'translateY(-2px)',
            '& .drag-handle': { opacity: 1 },
            '& .move-arrow': { opacity: 1 },
          },
        }}
      >
        {/* Drag Handle - Área para arrastar o card */}
        <Box
          {...listeners}
          className="drag-handle"
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            cursor: 'grab',
            opacity: 0.4,
            transition: 'opacity 0.2s',
            '&:active': { cursor: 'grabbing' },
            '&:hover': { opacity: 0.8 },
          }}
        >
          <DragIndicatorIcon fontSize="small" sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }} />
        </Box>

        {/* Setas para mover entre listas */}
        {prevList && onMoveCard && (
          <IconButton
            className="move-arrow"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMoveCard(card.id, prevList.id);
            }}
            sx={{
              position: 'absolute',
              left: '50%',
              top: 8,
              transform: 'translateX(calc(-50% - 20px))',
              opacity: 0.2,
              transition: 'all 0.2s',
              width: 28,
              height: 28,
              zIndex: 10,
              color: 'primary.main',
              '&:hover': {
                opacity: 0.9,
                bgcolor: 'action.hover',
                transform: 'translateX(calc(-50% - 20px)) scale(1.15)',
              },
            }}
          >
            <ArrowLeftIcon fontSize="small" />
          </IconButton>
        )}
        {nextList && onMoveCard && (
          <IconButton
            className="move-arrow"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMoveCard(card.id, nextList.id);
            }}
            sx={{
              position: 'absolute',
              left: '50%',
              top: 8,
              transform: 'translateX(calc(-50% + 20px))',
              opacity: 0.2,
              transition: 'all 0.2s',
              width: 28,
              height: 28,
              zIndex: 10,
              color: 'primary.main',
              '&:hover': {
                opacity: 0.9,
                bgcolor: 'action.hover',
                transform: 'translateX(calc(-50% + 20px)) scale(1.15)',
              },
            }}
          >
            <ArrowRightIcon fontSize="small" />
          </IconButton>
        )}

        {/* Botão para abrir o modal */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setOpenModal(true);
          }}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            opacity: 0.7,
            zIndex: 10,
            color: 'primary.main',
            '&:hover': { opacity: 1, bgcolor: 'action.hover' },
          }}
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
        {/* Conteúdo do card com padding para os botões */}
        <Box sx={{ pt: 3 }}>
          {/* Cover Image */}
          {card.cover_image_url && (
            <Box
              sx={{
                width: '100%',
                height: 100,
                backgroundImage: `url(${card.cover_image_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: 1,
                mb: 1,
              }}
            />
          )}

          {/* Labels */}
          {card.labels && card.labels.length > 0 && (
            <Box display="flex" gap={0.5} mb={1} flexWrap="wrap">
            {(Array.isArray(card.labels) ? card.labels : []).slice(0, 3).map((label: any, index: number) => {
              // Se label é um objeto com id e color
              if (label && typeof label === 'object' && label.id) {
                return (
                  <Chip
                    key={label.id}
                    label=""
                    size="small"
                    sx={{
                      height: 8,
                      width: 40,
                      backgroundColor: label.color || '#61BD4F',
                      '& .MuiChip-label': {
                        display: 'none',
                      },
                    }}
                  />
                );
              }
              // Se label é apenas um ID (fallback)
              return (
                <Chip
                  key={label || index}
                  label=""
                  size="small"
                  sx={{
                    height: 8,
                    width: 40,
                    backgroundColor: '#61BD4F',
                    '& .MuiChip-label': {
                      display: 'none',
                    },
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* Title */}
        <Typography variant="body2" sx={{ mb: card.due_date ? 0.5 : 1, wordBreak: 'break-word', color: 'text.primary', fontWeight: 500 }}>
          {card.title}
        </Typography>

        {/* Due Date - abaixo do título em vermelho para urgência */}
        {card.due_date && (
          <Typography
            variant="caption"
            sx={{
              color: isOverdue ? 'error.main' : 'text.secondary',
              fontSize: '0.7rem',
              mb: 1,
              display: 'block',
            }}
          >
            {(() => {
              // Extrair apenas a data (YYYY-MM-DD) da string ISO para evitar problemas de timezone
              const dateStr = card.due_date;
              let dateOnly = dateStr;
              if (dateStr.includes('T')) {
                dateOnly = dateStr.split('T')[0];
              }
              const [year, month, day] = dateOnly.split('-').map(Number);
              // Usar UTC para garantir que a data seja exibida corretamente
              const date = new Date(Date.UTC(year, month - 1, day));
              return format(date, 'dd MMM yyyy', { locale: ptBR });
            })()}
          </Typography>
        )}

        {/* Description Preview */}
        {card.description && (
          <Typography
            variant="caption"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mb: 1,
              color: 'text.secondary',
            }}
          >
            {card.description}
          </Typography>
        )}

        {/* Footer */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
          <Box display="flex" gap={1} alignItems="center">
            {card.is_completed && (
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            )}
          </Box>

          {/* Members */}
          {card.members && card.members.length > 0 && (
            <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24 } }}>
              {card.members.map((memberId) => (
                <Avatar key={memberId} sx={{ width: 24, height: 24, fontSize: 12 }}>
                  {memberId.substring(0, 2).toUpperCase()}
                </Avatar>
              ))}
            </AvatarGroup>
          )}
        </Box>

        {/* Cliente atribuído - parte inferior */}
        {card.client && card.client.company_name && (
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <BusinessIcon fontSize="small" sx={{ color: 'primary.main', fontSize: 14 }} />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {card.client.company_name}
            </Typography>
          </Box>
        )}

        {/* Setas para mover para cima/baixo - na base do card */}
        {(canMoveUp || canMoveDown) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 0.25,
              mt: 0.5,
              pt: 0.5,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            {canMoveUp && onMoveCardUp && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveCardUp(card.id);
                }}
                sx={{
                  opacity: 0.2,
                  width: 20,
                  height: 20,
                  minWidth: 20,
                  p: 0,
                  color: 'primary.main',
                  '&:hover': { opacity: 0.9, bgcolor: 'action.hover', transform: 'scale(1.15)' },
                }}
              >
                <ArrowUpIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
            {canMoveDown && onMoveCardDown && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveCardDown(card.id);
                }}
                sx={{
                  opacity: 0.2,
                  width: 20,
                  height: 20,
                  minWidth: 20,
                  p: 0,
                  color: 'primary.main',
                  '&:hover': { opacity: 0.9, bgcolor: 'action.hover', transform: 'scale(1.15)' },
                }}
              >
                <ArrowDownIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        )}
        </Box>
      </Box>

      {/* Modal */}
      <CardModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        cardId={card.id}
      />
    </>
  );
};

export default KanbanCard;

